/**
 * React Hooks for Notifications
 *
 * Hooks for managing notification state and operations
 */

'use client';

import {
  useQuery,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { gatedAction } from '@/lib/api/auth-gate';
import { useProfileQuery } from '@/lib/services/profile/hooks';
import { isBillingOnly } from '@/lib/utils/permissions';
import { createMutationHook, extractMutationData } from '../mutation-utils';
import {
  getNotificationsAction,
  getUnreadCountAction,
  getMattersWithUnreadAction,
  markNotificationReadAction,
  markAllNotificationsReadAction
} from './actions';
import {
  getNotificationsCacheKey,
  getUnreadCountCacheKey,
  getMattersWithUnreadCacheKey
} from './utils';
import type {
  Notification,
  NotificationsListResponse,
  UnreadCountResponse,
  MattersWithUnreadResponse
} from './types';

// Query Hooks

/**
 * Whether the current user may call the `/notifications` endpoints.
 *
 * Billing-only contacts (LF-3963 / LF-4077) are 403'd on the whole
 * `/notifications` family by the backend BILLING_ONLY_RESTRICTED guard, so we
 * skip the request entirely rather than fire it just to get a 403. Reads the
 * shared `/profile` query (deduplicated — no extra fetch, and it works even for
 * consumers like RecordingCompletionToaster that sit outside
 * UserProfileProvider). Stays disabled until the profile resolves so we never
 * fire for a billing-only user during the initial load.
 */
function useNotificationsAllowed(): boolean {
  const { data: profile } = useProfileQuery();
  if (!profile) return false;
  return !isBillingOnly(profile.user, profile.permissions);
}

/**
 * Hook to fetch notifications
 */
export function useNotifications(
  options?: Omit<
    UseQueryOptions<NotificationsListResponse>,
    'queryKey' | 'queryFn'
  >
) {
  const allowed = useNotificationsAllowed();
  return useQuery({
    queryKey: getNotificationsCacheKey(),
    queryFn: () => gatedAction(() => getNotificationsAction()),
    staleTime: 1 * 60 * 1000, // 1 minute (notifications should be fresh)
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes (reduced from 30 seconds)
    ...options,
    enabled: allowed && (options?.enabled ?? true)
  });
}

/**
 * Hook to fetch unread notification count
 */
export function useUnreadCount(
  options?: Omit<UseQueryOptions<UnreadCountResponse>, 'queryKey' | 'queryFn'>
) {
  const allowed = useNotificationsAllowed();
  return useQuery({
    queryKey: getUnreadCountCacheKey(),
    queryFn: () => gatedAction(() => getUnreadCountAction()),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes (reduced from 30 seconds)
    ...options,
    enabled: allowed && (options?.enabled ?? true)
  });
}

/**
 * Hook to fetch matters with unread notifications
 */
export function useMattersWithUnread(
  options?: Omit<
    UseQueryOptions<MattersWithUnreadResponse>,
    'queryKey' | 'queryFn'
  >
) {
  const allowed = useNotificationsAllowed();
  return useQuery({
    queryKey: getMattersWithUnreadCacheKey(),
    queryFn: () => gatedAction(() => getMattersWithUnreadAction()),
    staleTime: 1 * 60 * 1000, // 1 minute
    ...options,
    enabled: allowed && (options?.enabled ?? true)
  });
}

// Mutation Hooks

/**
 * Hook to mark notification as read
 */
export function useMarkNotificationRead(
  options?: UseMutationOptions<Notification, Error, number>
) {
  const useHook = createMutationHook<Notification, number>({
    mutationFn: async (notificationId) => {
      const result = await markNotificationReadAction(notificationId);
      return extractMutationData(result);
    },
    refetchKeys: () => [
      getNotificationsCacheKey(),
      getUnreadCountCacheKey(),
      getMattersWithUnreadCacheKey()
    ],
    showToast: false // Don't show toast for marking as read
  });

  return useHook(options);
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsRead(
  options?: UseMutationOptions<void, Error, void>
) {
  const useHook = createMutationHook<void, void>({
    mutationFn: async () => {
      const result = await markAllNotificationsReadAction();
      if (!result.success) {
        throw new Error(
          result.error || 'Failed to mark all notifications as read'
        );
      }
    },
    refetchKeys: () => [['notifications']],
    onSuccessMessage: () => 'All notifications marked as read',
    onErrorMessage: 'Failed to mark all notifications as read'
  });

  return useHook(options);
}
